import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/**
 * NavDropdown — supports two variants:
 *  - default: simple list of links
 *  - "carta": rich panel with a quiz teaser banner on top + iconified items
 */
export default function NavDropdown({ label, items, dataTestid, variant = 'default' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  // close on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  // close on outside click
  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const isActive = items.some((it) => pathname.startsWith(it.to));
  const isCarta = variant === 'carta';

  return (
    <li className="has-drop" ref={ref}>
      <button
        type="button"
        className={`nav-drop-trigger ${isActive ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={dataTestid}
      >
        {label}
        <svg width="9" height="6" viewBox="0 0 9 6" aria-hidden="true" style={{ marginLeft: 6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <path d="M1 1 L4.5 5 L8 1" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isCarta ? (
        <div className={`nav-drop-carta ${open ? 'open' : ''}`} data-testid={`${dataTestid}-menu`}>
          <NavLink
            to="/gildas#quiz"
            onClick={() => setOpen(false)}
            className="nav-drop-quiz"
            data-testid="nav-drop-quiz"
          >
            <span className="nav-drop-quiz-eyebrow">Descubre tu gilda</span>
            <span className="nav-drop-quiz-title">¿Cuál es <em>tu Gilda?</em></span>
            <span className="nav-drop-quiz-meta">12 variedades · 3 preguntas · 30 segundos</span>
            <span className="nav-drop-quiz-arrow" aria-hidden="true">→</span>
          </NavLink>
          <ul className="nav-drop-items">
            {items.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end
                  onClick={() => setOpen(false)}
                  className={({ isActive: a }) => a ? 'active' : ''}
                  data-testid={`nav-${it.to.replace('/', '')}`}
                >
                  <span className="nav-drop-icon" aria-hidden="true">
                  {it.img ? (
                    <img src={it.img} alt="" loading="lazy" />
                  ) : (
                    <span className="nav-drop-icon-emoji">{it.icon || '·'}</span>
                  )}
                </span>
                  <span className="nav-drop-text">
                    <span className="nav-drop-label">{it.label}</span>
                    {it.hint && <span className="nav-drop-hint">{it.hint}</span>}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <ul className={`nav-drop ${open ? 'open' : ''}`} data-testid={`${dataTestid}-menu`}>
          {items.map((it) => (
            <li key={it.to}>
              <NavLink
                to={it.to}
                end
                onClick={() => setOpen(false)}
                className={({ isActive: a }) => (a ? 'active' : '')}
                data-testid={`nav-${it.to.replace('/', '')}`}
              >
                {it.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
